/**
 * Credit Block file attachment React Query hooks.
 * Uses Gobodo FilesApi (listFiles, uploadFileBase64, getFileById) when USE_GOBODO_API is true.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import * as gobodoFileService from '../gobodo/gobodoFileService';
import { getRelationshipTypeKeysForList, getRelationshipTypeKeyForCategory, getAllFileRelationshipTypeKeys } from '../../config/gobodoCreditBlockFileRelations';
import type { CreditBlockFile } from '../../types';

/** Counts per file category (from countFiles API). */
export interface CreditBlockFileCounts {
  all: number;
  identification: number;
  reports: number;
  correspondence: number;
  contracts: number;
}

export type CreditBlockFileCategory =
  | 'all'
  | 'identification'
  | 'reports'
  | 'correspondence'
  | 'contracts';

export const CREDIT_BLOCK_FILES_QUERY_KEY = ['credit-block', 'files'] as const;

function queryKeyForClient(clientId: string, fileCategory?: CreditBlockFileCategory): (string | CreditBlockFileCategory)[] {
  const base = [...CREDIT_BLOCK_FILES_QUERY_KEY, clientId];
  return fileCategory != null ? [...base, fileCategory] : base;
}

/**
 * Fetch files attached to a Credit Block client (Gobodo only).
 * When fileCategory is provided, listFiles is called with the corresponding relationshipTypeKey(s).
 * When fileCategory is omitted (e.g. main app client files tab), uses 'all' so listFiles gets all three
 * custom relationship types plus CONTAINER.
 * When USE_GOBODO_API is false or enabled is false, returns undefined and does not run.
 */
export function useCreditBlockFiles(
  clientId: string,
  enabled = true,
  fileCategory?: CreditBlockFileCategory
) {
  return useQuery({
    queryKey: queryKeyForClient(clientId, fileCategory),
    queryFn: () => {
      const category = fileCategory ?? 'all';
      const relationshipTypeKeys = getRelationshipTypeKeysForList(category);
      return gobodoFileService.listFiles(clientId, {
        ...(relationshipTypeKeys.length > 0 && { relationshipTypeKey: relationshipTypeKeys }),
      });
    },
    enabled: Boolean(USE_GOBODO_API && clientId && enabled),
    staleTime: 60_000,
  });
}

export interface UploadCreditBlockFileVariables {
  file: File;
  description?: string;
  /** When provided, upload uses this relationship type key (e.g. current tab). */
  reltypekey?: string | null;
}

/**
 * Upload a file to a Credit Block client (Gobodo only).
 * Pass reltypekey (or fileCategory) so the file is associated with the correct relationship type.
 * On success, invalidates the files list for that client (all categories).
 */
export function useUploadCreditBlockFile(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, description, reltypekey }: UploadCreditBlockFileVariables) => {
      return gobodoFileService.uploadFile(clientId, file, { description, reltypekey: reltypekey ?? undefined });
    },
    onSuccess: (_data, _variables, _context) => {
      queryClient.invalidateQueries({ queryKey: [...CREDIT_BLOCK_FILES_QUERY_KEY, clientId] });
      queryClient.invalidateQueries({ queryKey: [...CREDIT_BLOCK_FILES_QUERY_KEY, 'counts', clientId] });
    },
  });
}

const CREDIT_BLOCK_FILES_COUNTS_QUERY_KEY = [...CREDIT_BLOCK_FILES_QUERY_KEY, 'counts'] as const;

/**
 * Fetch file counts for all categories (all, identification, reports, correspondence, contracts) using countFiles().
 * Used to display counts in the category tabs when USE_GOBODO_API is true.
 */
export function useCreditBlockFileCounts(clientId: string, enabled = true) {
  return useQuery({
    queryKey: [...CREDIT_BLOCK_FILES_COUNTS_QUERY_KEY, clientId],
    queryFn: async (): Promise<CreditBlockFileCounts> => {
      const [all, identification, reports, correspondence, contracts] = await Promise.all([
        gobodoFileService.countFiles(clientId, { relationshipTypeKey: getAllFileRelationshipTypeKeys() }),
        gobodoFileService.countFiles(clientId, {
          relationshipTypeKey: [getRelationshipTypeKeyForCategory('identification')!],
        }),
        gobodoFileService.countFiles(clientId, {
          relationshipTypeKey: [getRelationshipTypeKeyForCategory('reports')!],
        }),
        gobodoFileService.countFiles(clientId, {
          relationshipTypeKey: [getRelationshipTypeKeyForCategory('correspondence')!],
        }),
        gobodoFileService.countFiles(clientId, {
          relationshipTypeKey: [getRelationshipTypeKeyForCategory('contracts')!],
        }),
      ]);
      return { all, identification, reports, correspondence, contracts };
    },
    enabled: Boolean(USE_GOBODO_API && clientId && enabled),
    staleTime: 60_000,
  });
}

/** Resolve file category to reltypekey for upload (single key or null for default). */
export function getRelTypeKeyForUpload(fileCategory: CreditBlockFileCategory): string | null {
  return getRelationshipTypeKeyForCategory(fileCategory);
}

/**
 * Fetch a single file by id (for metadata / download).
 * Use when you need file details or a download URL if the API provides one.
 */
export function useCreditBlockFileById(fileId: number | null, enabled = true) {
  return useQuery({
    queryKey: [...CREDIT_BLOCK_FILES_QUERY_KEY, 'byId', fileId],
    queryFn: () => gobodoFileService.getFileById(fileId!),
    enabled: Boolean(USE_GOBODO_API && fileId != null && enabled),
    staleTime: 30_000,
  });
}

/**
 * Helper to trigger file download using getFileById.
 * If the API returns a download URL we use it; otherwise we only have metadata.
 * Caller can use returned CreditBlockFile.downloadUrl if present.
 */
export async function getFileForDownload(fileId: number): Promise<CreditBlockFile> {
  return gobodoFileService.getFileById(fileId);
}
